import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { createWearableMetric, getWearableSummary, getWearableMetrics, analyzeWearables } from '@/services/api';
import { toast } from 'sonner';
import { Activity, Watch, Sparkles, Target } from 'lucide-react';

const deviceOptions = [
  'Apple Health',
  'Whoop',
  'Fitbit',
  'Samsung Health',
  'Garmin',
  'Manual Entry'
];

export default function DailyHealthPage() {
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [goals, setGoals] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    device_source: 'Apple Health',
    steps: '',
    calories: '',
    sleep_hours: '',
    heart_rate_avg: '',
    pulse_avg: '',
    active_minutes: '',
    notes: ''
  });

  useEffect(() => {
    loadWearableData();
  }, []);

  const loadWearableData = async () => {
    try {
      const [summaryData, metricsData] = await Promise.all([
        getWearableSummary(),
        getWearableMetrics()
      ]);
      setSummary(summaryData);
      setMetrics(metricsData);
    } catch (error) {
      toast.error('Unable to load daily health data');
    }
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleDeviceChange = (value) => {
    setFormData((prev) => ({ ...prev, device_source: value }));
  };

  const handleSaveMetric = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await createWearableMetric({
        ...formData,
        steps: formData.steps ? Number(formData.steps) : null,
        calories: formData.calories ? Number(formData.calories) : null,
        sleep_hours: formData.sleep_hours ? Number(formData.sleep_hours) : null,
        heart_rate_avg: formData.heart_rate_avg ? Number(formData.heart_rate_avg) : null,
        pulse_avg: formData.pulse_avg ? Number(formData.pulse_avg) : null,
        active_minutes: formData.active_minutes ? Number(formData.active_minutes) : null
      });
      toast.success('Daily health metrics saved');
      setFormData((prev) => ({
        ...prev,
        steps: '',
        calories: '',
        sleep_hours: '',
        heart_rate_avg: '',
        pulse_avg: '',
        active_minutes: '',
        notes: ''
      }));
      await loadWearableData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const goalList = goals ? goals.split(',').map(item => item.trim()).filter(Boolean) : [];
      const result = await analyzeWearables({ goals: goalList });
      if (result.error) {
        toast.error(result.error);
      } else {
        setAnalysis(result);
        toast.success('Insights generated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to analyze metrics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="daily-health-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Health Monitoring
          </h1>
          <p className="text-slate-500">
            Wearable data is tracked separately from medical records and used for lifestyle guidance.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="bg-white border-slate-100 shadow-sm" data-testid="wearable-summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-900" />Latest Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {summary?.latest ? (
                <>
                  <div className="text-sm text-slate-500" data-testid="wearable-latest-date">{summary.latest.date} • {summary.latest.device_source}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Steps</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="wearable-latest-steps">{summary.latest.steps ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Calories</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="wearable-latest-calories">{summary.latest.calories ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Sleep (hrs)</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="wearable-latest-sleep">{summary.latest.sleep_hours ?? '--'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Heart Rate</p>
                      <p className="text-lg font-semibold text-slate-900" data-testid="wearable-latest-heart">{summary.latest.heart_rate_avg ?? '--'}</p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500" data-testid="wearable-no-data">No wearable metrics yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-sm" data-testid="wearable-average-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-900" />30-Day Average
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {summary ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Steps</p>
                    <p className="text-lg font-semibold text-slate-900" data-testid="wearable-avg-steps">{summary.averages?.steps ?? '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Calories</p>
                    <p className="text-lg font-semibold text-slate-900" data-testid="wearable-avg-calories">{summary.averages?.calories ?? '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Sleep</p>
                    <p className="text-lg font-semibold text-slate-900" data-testid="wearable-avg-sleep">{summary.averages?.sleep_hours ?? '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Active Min</p>
                    <p className="text-lg font-semibold text-slate-900" data-testid="wearable-avg-active">{summary.averages?.active_minutes ?? '--'}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">No data yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-sm" data-testid="wearable-connect-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Watch className="w-5 h-5 text-blue-900" />Connect Wearables
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {deviceOptions.slice(0, 4).map((device) => (
                <div key={device} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`wearable-connect-${device.replace(/\s+/g, '-').toLowerCase()}`}>
                  <span className="text-sm text-slate-700">{device}</span>
                  <Badge variant="secondary">Credentials Pending</Badge>
                </div>
              ))}
              <p className="text-xs text-slate-500">
                Device integrations will activate once API credentials are provided.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-white border-slate-100 shadow-sm" data-testid="wearable-entry-card">
            <CardHeader>
              <CardTitle>Log Daily Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveMetric} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wearableDate">Date</Label>
                    <Input
                      id="wearableDate"
                      type="date"
                      value={formData.date}
                      onChange={handleChange('date')}
                      data-testid="wearable-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Device Source</Label>
                    <Select value={formData.device_source} onValueChange={handleDeviceChange}>
                      <SelectTrigger data-testid="wearable-device-select">
                        <SelectValue placeholder="Select device" />
                      </SelectTrigger>
                      <SelectContent>
                        {deviceOptions.map((device) => (
                          <SelectItem key={device} value={device} data-testid={`wearable-device-${device.replace(/\s+/g, '-').toLowerCase()}`}>
                            {device}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="steps">Steps</Label>
                    <Input id="steps" type="number" value={formData.steps} onChange={handleChange('steps')} data-testid="wearable-steps-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="calories">Calories</Label>
                    <Input id="calories" type="number" value={formData.calories} onChange={handleChange('calories')} data-testid="wearable-calories-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sleep">Sleep Hours</Label>
                    <Input id="sleep" type="number" step="0.1" value={formData.sleep_hours} onChange={handleChange('sleep_hours')} data-testid="wearable-sleep-input" />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="heartRate">Avg Heart Rate</Label>
                    <Input id="heartRate" type="number" value={formData.heart_rate_avg} onChange={handleChange('heart_rate_avg')} data-testid="wearable-heart-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pulse">Avg Pulse</Label>
                    <Input id="pulse" type="number" value={formData.pulse_avg} onChange={handleChange('pulse_avg')} data-testid="wearable-pulse-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="active">Active Minutes</Label>
                    <Input id="active" type="number" value={formData.active_minutes} onChange={handleChange('active_minutes')} data-testid="wearable-active-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea id="notes" rows={3} value={formData.notes} onChange={handleChange('notes')} data-testid="wearable-notes-input" />
                </div>

                <Button type="submit" className="bg-blue-900 hover:bg-blue-800 text-white" disabled={loading} data-testid="wearable-save-btn">
                  {loading ? 'Saving...' : 'Save Daily Metrics'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-sm" data-testid="wearable-insights-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-900" />AI Lifestyle Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="goals">Goals (comma separated)</Label>
                <Input
                  id="goals"
                  value={goals}
                  onChange={(event) => setGoals(event.target.value)}
                  placeholder="Improve sleep, 10k steps, better recovery"
                  data-testid="wearable-goals-input"
                />
              </div>
              <Button
                className="bg-blue-900 hover:bg-blue-800 text-white"
                onClick={handleAnalyze}
                disabled={loading}
                data-testid="wearable-analyze-btn"
              >
                {loading ? 'Analyzing...' : 'Generate Insights'}
              </Button>

              {analysis && (
                <div className="space-y-3" data-testid="wearable-analysis-result">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Summary</p>
                    <p className="text-sm text-slate-600" data-testid="wearable-analysis-summary">{analysis.summary}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Insights</p>
                    <ul className="list-disc list-inside text-sm text-slate-600" data-testid="wearable-analysis-insights">
                      {analysis.insights?.length ? analysis.insights.map((item, index) => (
                        <li key={index}>{item}</li>
                      )) : <li>No insights yet.</li>}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Recommendations</p>
                    <ul className="list-disc list-inside text-sm text-slate-600" data-testid="wearable-analysis-recommendations">
                      {analysis.recommendations?.length ? analysis.recommendations.map((item, index) => (
                        <li key={index}>{item}</li>
                      )) : <li>No recommendations yet.</li>}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}